import React from "react";
import {
  ArrowUpDown,
  CheckCircle,
  Copy,
  Download,
  FileText,
  LayoutGrid,
  List,
  Plus,
  Search,
  Trash2,
  User,
} from "lucide-react";
import CustomSelect from "../../components/CustomSelect";
import AppInput from "../../components/ui/AppInput";
import ThemeTooltip from "../../components/ThemeTooltip";
import {
  STUDENT_LIST_HEADER_DOWNLOAD_MENU_CLASS,
  STUDENT_LIST_HEADER_DOWNLOAD_MENU_ITEM_CLASS,
  STUDENT_LIST_HEADER_DOWNLOAD_TRIGGER_CLASS,
  STUDENT_LIST_HEADER_LEFT_GROUP_CLASS,
  STUDENT_LIST_HEADER_REGISTER_MENU_CLASS,
  STUDENT_LIST_HEADER_REGISTER_MENU_ITEM_CLASS,
  STUDENT_LIST_HEADER_RIGHT_ACTIONS_CLASS,
  STUDENT_LIST_HEADER_SEARCH_ICON_CLASS,
  STUDENT_LIST_HEADER_SEARCH_WRAPPER_CLASS,
  STUDENT_LIST_HEADER_SELECT_SORT_GROUP_CLASS,
  STUDENT_LIST_HEADER_TITLE_ROW_CLASS,
  STUDENT_LIST_HEADER_TOOLBAR_CLASS,
  STUDENT_LIST_HEADER_VIEW_TOGGLE_WRAP_CLASS,
  getStudentListHeaderBulkDeleteButtonClass,
  getStudentListHeaderRegisterButtonClass,
  getStudentListHeaderSearchInputClass,
  getStudentListHeaderSelectAllButtonClass,
  getStudentListHeaderSelectAllIconClass,
  getStudentListHeaderSortToggleClass,
  getStudentListHeaderViewModeButtonClass,
} from "./studentListHeaderStyles";

export default function StudentListHeaderToolbar({
  icons,
  sortedStudentsLength,
  theme,
  searchTerm,
  setSearchTerm,
  selectedDepartment,
  filterDept,
  setFilterDept,
  userProfile,
  myDepartments,
  sortKey,
  setSortKey,
  sortOrder,
  setSortOrder,
  selectedIdsLength,
  handleSelectAllToggle,
  viewMode,
  setViewMode,
  isTeacher,
  handleBulkDelete,
  isDownloadMenuOpen,
  setIsDownloadMenuOpen,
  handleDownloadExcel,
  handleCopyStats,
  registerMenuRef,
  isRegisterMenuOpen,
  setIsRegisterMenuOpen,
  openAddModal,
  setIsBulkModalOpen,
  hideTitle = false,
}) {
  return (
    <>
      {!hideTitle && (
        <div className={STUDENT_LIST_HEADER_TITLE_ROW_CLASS}>
          <div>
            <h2 className="text-3xl font-black text-[#2C3E30] flex items-center gap-3 font-do-hyeon">
              <img src={icons.student} width={48} alt="icon" className="drop-shadow-md" /> 학생 명단
            </h2>
            <p className="text-gray-500 text-sm font-medium mt-1 ml-1">
              총 <span className="font-bold text-[#354F38]">{sortedStudentsLength}</span>명의 학생을 체계적으로 관리하세요.
            </p>
          </div>
        </div>
      )}

      <div className={STUDENT_LIST_HEADER_TOOLBAR_CLASS}>
        <div className={STUDENT_LIST_HEADER_LEFT_GROUP_CLASS}>
          <AppInput
            id="student-list-search"
            name="student_list_search"
            placeholder="이름, 학교 검색"
            className={getStudentListHeaderSearchInputClass(theme)}
            containerClassName="flex-1 max-w-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            icon={Search}
          />

          {(selectedDepartment === "All" || selectedDepartment === "전체") && (
            <CustomSelect
              className="w-[140px] flex-shrink-0"
              value={filterDept}
              onChange={setFilterDept}
              options={[
                { value: "전체", label: "전체 부서" },
                ...(userProfile?.deptName ? [{ value: userProfile.deptName, label: userProfile.deptName }] : []),
                ...myDepartments.filter((d) => d !== userProfile?.deptName).map((d) => ({ value: d, label: d })),
                { value: "기타", label: "기타" },
              ]}
              placeholder="부서 선택"
              theme={theme}
            />
          )}

          <div className={STUDENT_LIST_HEADER_SELECT_SORT_GROUP_CLASS}>
            <CustomSelect
              className="w-[120px] flex-shrink-0"
              value={sortKey}
              onChange={setSortKey}
              options={[
                { value: "name", label: "이름순" },
                { value: "grade", label: "학년순" },
                { value: "class", label: "반별순" },
              ]}
              showSearch={false}
              placeholder="정렬"
              theme={theme}
            />
            <button
              onClick={() => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}
              className={getStudentListHeaderSortToggleClass(sortOrder, theme.text)}
              title={sortOrder === "asc" ? "오름차순" : "내림차순"}
            >
              <ArrowUpDown size={18} className={sortOrder === "desc" ? "rotate-180" : ""} />
            </button>
          </div>

          <div className={STUDENT_LIST_HEADER_VIEW_TOGGLE_WRAP_CLASS}>
            <ThemeTooltip text="전체 선택" theme={theme}>
              <button
                onClick={handleSelectAllToggle}
                className={getStudentListHeaderSelectAllButtonClass(selectedIdsLength)}
              >
                <CheckCircle size={18} className={getStudentListHeaderSelectAllIconClass(selectedIdsLength)} />
                {selectedIdsLength > 0 && <span className="text-[10px] font-bold">{selectedIdsLength}</span>}
              </button>
            </ThemeTooltip>
            <div className="w-px bg-gray-200 mx-1 h-6"></div>
            <ThemeTooltip text="카드형 보기" theme={theme}>
              <button
                onClick={() => setViewMode("card")}
                className={getStudentListHeaderViewModeButtonClass(viewMode === "card", theme)}
              >
                <LayoutGrid size={18} />
              </button>
            </ThemeTooltip>
            <ThemeTooltip text="리스트형 보기" theme={theme}>
              <button
                onClick={() => setViewMode("list")}
                className={getStudentListHeaderViewModeButtonClass(viewMode === "list", theme)}
              >
                <List size={18} />
              </button>
            </ThemeTooltip>
          </div>
        </div>

        <div className={STUDENT_LIST_HEADER_RIGHT_ACTIONS_CLASS}>
          {!isTeacher && (
            <button
              onClick={handleBulkDelete}
              disabled={selectedIdsLength === 0}
              className={getStudentListHeaderBulkDeleteButtonClass(selectedIdsLength)}
            >
              <Trash2 size={20} />
            </button>
          )}

          {!isTeacher && (
            <div className="relative">
              <button
                onClick={() => setIsDownloadMenuOpen(!isDownloadMenuOpen)}
                className={STUDENT_LIST_HEADER_DOWNLOAD_TRIGGER_CLASS}
                title="다운로드 및 공유"
              >
                <Download size={20} />
              </button>
              {isDownloadMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsDownloadMenuOpen(false)}></div>
                  <div className={STUDENT_LIST_HEADER_DOWNLOAD_MENU_CLASS}>
                    <button
                      onClick={() => {
                        handleDownloadExcel();
                        setIsDownloadMenuOpen(false);
                      }}
                      className={STUDENT_LIST_HEADER_DOWNLOAD_MENU_ITEM_CLASS}
                    >
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                        <FileText size={16} />
                      </div>
                      엑셀로 저장
                    </button>
                    <button
                      onClick={() => {
                        handleCopyStats();
                        setIsDownloadMenuOpen(false);
                      }}
                      className={STUDENT_LIST_HEADER_DOWNLOAD_MENU_ITEM_CLASS}
                    >
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                        <Copy size={16} />
                      </div>
                      현황 복사
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="relative" ref={registerMenuRef}>
            <button
              onClick={() => setIsRegisterMenuOpen(!isRegisterMenuOpen)}
              className={getStudentListHeaderRegisterButtonClass(theme.bg)}
            >
              <Plus size={20} /> <span className="hidden sm:inline">등록</span>
            </button>
            {isRegisterMenuOpen && (
              <div className={STUDENT_LIST_HEADER_REGISTER_MENU_CLASS}>
                <button
                  onClick={() => {
                    setIsRegisterMenuOpen(false);
                    openAddModal();
                  }}
                  className={STUDENT_LIST_HEADER_REGISTER_MENU_ITEM_CLASS}
                >
                  <User size={18} className={theme.text} /> 개별 등록
                </button>
                <button
                  onClick={() => {
                    setIsRegisterMenuOpen(false);
                    setIsBulkModalOpen(true);
                  }}
                  className={`${STUDENT_LIST_HEADER_REGISTER_MENU_ITEM_CLASS} border-t border-gray-50`}
                >
                  <FileText size={18} className={theme.text} /> 일괄 등록
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
